import { useMemo } from 'react';

import { Flex, VFlex } from '@/components/base';
import { ENTIRE_ONLY_FIELDS } from '@/entities/chart/model/constants';
import useFieldConfig from '@/features/(charts)/chart-controller/hooks/use-field-config';
import { ChartFieldFilterKeyMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import useColor from '@/hooks/domain/useColor';
import { radius } from '@/styles/sizes';

import ChartFieldSelect from '../ChartFieldSelect';
import DimensionTopLabel from './top-label';

type Props = {
  chartInfo: ChartContextProps;
  targetField: ChartFieldDef;
  switchFlag: number;
  isColumn: boolean;
  allowTop?: boolean;
  onFieldItemChange: (values: StringNumberValue[]) => void;
  onTopLabelClick: () => void;
  onHeaderTopLabelClick: (onDeselectAll: () => void, close: () => void) => void;
  onClear: () => void;
};

export default function DimensionFieldSelector({
  chartInfo,
  targetField,
  switchFlag,
  isColumn,
  allowTop,
  onFieldItemChange,
  onTopLabelClick,
  onHeaderTopLabelClick,
  onClear,
}: Props) {
  const color = useColor();
  const fieldConfigs = useFieldConfig();

  const currField = useMemo(() => {
    const filter = fieldConfigs.find(
      (config) => config.columnKey === ChartFieldFilterKeyMap[targetField.type],
    );
    return filter || null;
  }, [fieldConfigs, targetField.type]);

  const supportsCustomChartInput = !!currField?.ChartFilterItem;

  if (ENTIRE_ONLY_FIELDS.has(targetField.type) && !supportsCustomChartInput) {
    return null;
  }

  return (
    <VFlex>
      {targetField.entire && (
        <Flex
          style={{
            border: `1px solid ${color.gray400}`,
            borderRadius: radius.token.md,
          }}
        >
          <DimensionTopLabel
            field={chartInfo.state.field.type}
            onClick={onTopLabelClick}
          />
        </Flex>
      )}
      <div
        style={{
          height: targetField.entire ? 0 : 'auto',
          overflow: 'hidden',
        }}
      >
        <ChartFieldSelect
          key={`${targetField.type}-${switchFlag}`}
          initialized={true}
          filterOption={currField}
          values={targetField.candidates || []}
          onChange={onFieldItemChange}
          metaValues={chartInfo.tempMetaValues || []}
          loading={chartInfo.tableQuery.isFetching}
          header={
            !isColumn && allowTop
              ? ({ onDeselectAll, close }) => (
                  <DimensionTopLabel
                    field={chartInfo.state.field.type}
                    onClick={() => onHeaderTopLabelClick(onDeselectAll, close)}
                  />
                )
              : undefined
          }
          showTopAction={!isColumn && allowTop}
          onClear={onClear}
        />
      </div>
    </VFlex>
  );
}
